import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";
import { Send, Trash2, Image as ImageIcon, User, Upload, MessageSquare, Loader2, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function mediaUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/media/")) return `${BACKEND}${pathOrUrl}`;
  return pathOrUrl;
}

function ImgSigned({ path, className, alt }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api.post("/media/sign", { paths: [path] });
        const url = r.data?.urls?.[path];
        if (!dead && url) setSrc(url);
      } catch (_e) {}
    })();
    return () => { dead = true; };
  }, [path]);
  if (!src) return <div className={`${className} bg-white/5`} />;
  return <img src={src} alt={alt || ""} className={className} />;
}

function SetupPanel({ onCreated }) {
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [age, setAge] = useState(26);
  const [avatar, setAvatar] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Skriv inn et brukernavn");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("username", username.trim());
      fd.append("bio", bio.trim());
      fd.append("age", String(age));
      if (avatar) fd.append("avatar", avatar);
      const r = await api.post("/admin/promo-mock/create", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Mock-brukeren er opprettet");
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke opprette");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="panel p-6 space-y-4 max-w-2xl" data-testid="promo-mock-setup">
      <div className="font-fraunces italic text-2xl text-[var(--cream)]">Opprett promo-mock</div>
      <p className="text-sm text-[var(--cream-soft)]">
        Én statisk konto som ser ut som en vanlig bruker på plattformen.
        Kan følges, kommenteres og motta DMer. Alle meldinger havner her.
        Kontoen kan ikke logge inn selv.
      </p>

      <div>
        <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
          BRUKERNAVN (3–20 tegn)
        </label>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={20}
          className="w-full bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
          placeholder="Julie00xxx"
          data-testid="promo-mock-username-input"
        />
      </div>

      <div>
        <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
          ALDER
        </label>
        <input
          type="number"
          min={18}
          max={99}
          value={age}
          onChange={(e) => setAge(parseInt(e.target.value) || 26)}
          className="w-32 bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
          data-testid="promo-mock-age-input"
        />
      </div>

      <div>
        <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
          BIO (maks 300 tegn)
        </label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={300}
          rows={3}
          className="w-full bg-[var(--noir)] border border-white/10 px-3 py-2 text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
          placeholder="Nysgjerrig jente på 26 som vil utforske litt mer med den rette"
          data-testid="promo-mock-bio-input"
        />
      </div>

      <div>
        <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">
          PROFILBILDE (VALGFRITT)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setAvatar(e.target.files?.[0] || null)}
          className="block text-xs text-[var(--cream-soft)] file:mr-4 file:py-2 file:px-4 file:border file:border-[var(--magenta)] file:text-[var(--magenta)] file:bg-transparent hover:file:bg-[var(--magenta)]/10"
          data-testid="promo-mock-avatar-input"
        />
      </div>

      <button
        type="submit"
        disabled={busy}
        className="px-5 py-2 text-xs font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
        data-testid="promo-mock-create-btn"
      >
        {busy ? "OPPRETTER…" : "OPPRETT MOCK"}
      </button>
    </form>
  );
}

function ProfileEditor({ mock, album, images, onReload }) {
  const [bio, setBio] = useState(mock.bio || "");
  const [savingBio, setSavingBio] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const avatarRef = useRef(null);
  const albumImgRef = useRef(null);

  const saveBio = async () => {
    setSavingBio(true);
    try {
      await api.patch("/admin/promo-mock/bio", { bio });
      toast.success("Bio lagret");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSavingBio(false);
    }
  };

  const uploadAvatar = async (file) => {
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      await api.post("/admin/promo-mock/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profilbilde oppdatert");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke laste opp");
    } finally {
      setUploadingAvatar(false);
      if (avatarRef.current) avatarRef.current.value = "";
    }
  };

  const uploadAlbumImage = async (file) => {
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("visibility", "vip");
      await api.post("/admin/promo-mock/album-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Bilde lagt til i VIP-albumet");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke laste opp");
    } finally {
      setUploadingImage(false);
      if (albumImgRef.current) albumImgRef.current.value = "";
    }
  };

  const deleteImage = async (imgId) => {
    if (!window.confirm("Slett dette bildet?")) return;
    try {
      await api.delete(`/admin/promo-mock/album-images/${imgId}`);
      toast.success("Slettet");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke slette");
    }
  };

  return (
    <div className="space-y-6" data-testid="promo-mock-profile-editor">
      <div className="panel p-5">
        <div className="flex items-start gap-5">
          <div className="w-24 h-24 bg-white/5 border border-white/10 overflow-hidden shrink-0 relative">
            {mock.avatar_url ? (
              <img src={mediaUrl(mock.avatar_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-full h-full p-6 text-white/30" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-fraunces italic text-xl text-[var(--cream)]">@{mock.username}</div>
            <div className="text-xs text-[var(--cream-soft)] mt-1">Alder: {mock.age}</div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <label className="px-3 py-1.5 text-[10px] font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)] cursor-pointer">
                <Upload className="w-3 h-3 inline mr-1" />
                {uploadingAvatar ? "LASTER…" : "BYTT PROFILBILDE"}
                <input
                  ref={avatarRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])}
                  data-testid="promo-mock-avatar-replace"
                />
              </label>
              <a
                href={`/profile/${mock.username}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 text-[10px] font-meta tracking-[0.15em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                data-testid="promo-mock-view-profile"
              >
                SE PROFIL PÅ SIDEN
              </a>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <label className="block text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mb-2">BIO</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={300}
            rows={2}
            className="w-full bg-[var(--noir)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none"
            data-testid="promo-mock-bio-edit"
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-[10px] text-[var(--cream-soft)]">{bio.length}/300</span>
            <button
              onClick={saveBio}
              disabled={savingBio}
              className="px-4 py-1.5 text-[10px] font-meta tracking-[0.15em] bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
              data-testid="promo-mock-save-bio"
            >
              {savingBio ? "LAGRER…" : "LAGRE BIO"}
            </button>
          </div>
        </div>
      </div>

      <div className="panel p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-fraunces italic text-lg text-[var(--cream)]">
              Album «{album?.title || "VIP"}»
            </div>
            <div className="text-[10px] font-meta tracking-[0.2em] text-[var(--cream-soft)] mt-1">
              KUN VIP-MEDLEMMER KAN SE — {images.length} BILDER
            </div>
          </div>
          <label className="px-3 py-2 text-[10px] font-meta tracking-[0.15em] border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white cursor-pointer">
            <Upload className="w-3 h-3 inline mr-1" />
            {uploadingImage ? "LASTER…" : "LEGG TIL BILDE"}
            <input
              ref={albumImgRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadAlbumImage(e.target.files[0])}
              data-testid="promo-mock-album-upload"
            />
          </label>
        </div>

        {images.length === 0 ? (
          <div className="text-sm text-[var(--cream-soft)] py-8 text-center">
            Ingen bilder i albumet ennå. Legg til et for at VIP-medlemmer skal ha noe å se.
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {images.map((img) => (
              <div key={img.id} className="relative group aspect-square bg-white/5 overflow-hidden">
                <ImgSigned path={img.storage_path} className="w-full h-full object-cover" alt="" />
                <button
                  onClick={() => deleteImage(img.id)}
                  className="absolute top-1 right-1 p-1 bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`promo-mock-delete-img-${img.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InboxPanel({ mock, onOpenConv }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/promo-mock/conversations");
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="text-sm text-[var(--cream-soft)]">Laster samtaler…</div>;

  if (rows.length === 0) {
    return (
      <div className="panel p-8 text-center" data-testid="promo-mock-inbox-empty">
        <MessageSquare className="w-8 h-8 mx-auto text-white/20 mb-3" />
        <div className="text-sm text-[var(--cream-soft)]">Ingen meldinger enda.</div>
        <div className="text-xs text-[var(--cream-soft)] mt-2 opacity-60">
          Del profilen «@{mock.username}» på Snap eller andre plattformer for å begynne å motta DMer.
        </div>
      </div>
    );
  }

  return (
    <div className="panel divide-y divide-white/5" data-testid="promo-mock-inbox">
      {rows.map((c) => (
        <button
          key={c.id}
          onClick={() => onOpenConv(c)}
          className="w-full flex items-center gap-3 p-4 hover:bg-white/5 text-left"
          data-testid={`promo-mock-conv-${c.id}`}
        >
          <div className="w-11 h-11 bg-white/5 border border-white/10 overflow-hidden shrink-0">
            {c.other?.avatar_url ? (
              <img src={mediaUrl(c.other.avatar_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-full h-full p-2 text-white/30" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--cream)] truncate">@{c.other?.username || "ukjent"}</span>
              {c.other?.tier === "vip" && (
                <span className="text-[9px] font-meta tracking-widest text-amber-400 border border-amber-400/40 px-1">VIP</span>
              )}
              {c.unread > 0 && (
                <span className="ml-auto text-[10px] font-meta tracking-widest bg-[var(--magenta)] text-white px-1.5 py-0.5">
                  {c.unread}
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--cream-soft)] truncate mt-0.5">{c.last_message || "…"}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatView({ conv, mock, onBack, onSent }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/promo-mock/conversations/${conv.id}/messages`);
      setMessages(r.data?.messages || []);
    } finally {
      setLoading(false);
    }
  }, [conv.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const b = body.trim();
    if (!b) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/promo-mock/conversations/${conv.id}/reply`, { body: b });
      setMessages((m) => [...m, r.data]);
      setBody("");
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="panel flex flex-col h-[70vh]" data-testid="promo-mock-chat">
      <div className="flex items-center gap-3 p-4 border-b border-white/5">
        <button onClick={onBack} className="text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="promo-mock-chat-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-9 h-9 bg-white/5 border border-white/10 overflow-hidden">
          {conv.other?.avatar_url ? (
            <img src={mediaUrl(conv.other.avatar_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <User className="w-full h-full p-2 text-white/30" />
          )}
        </div>
        <div>
          <div className="text-sm text-[var(--cream)]">@{conv.other?.username}</div>
          <div className="text-[10px] text-[var(--cream-soft)]">
            Du svarer som <span className="text-[var(--magenta)]">@{mock.username}</span>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="text-sm text-[var(--cream-soft)]"><Loader2 className="w-4 h-4 inline animate-spin" /> Laster…</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-[var(--cream-soft)] text-center py-8">Ingen meldinger enda.</div>
        ) : (
          messages.map((m) => {
            const fromMock = m.sender_id === mock.id;
            return (
              <div
                key={m.id}
                className={`flex ${fromMock ? "justify-end" : "justify-start"}`}
                data-testid={`promo-mock-msg-${m.id}`}
              >
                <div
                  className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap ${
                    fromMock
                      ? "bg-[var(--magenta)]/20 border border-[var(--magenta)]/40 text-[var(--cream)]"
                      : "bg-white/5 border border-white/10 text-[var(--cream)]"
                  }`}
                >
                  {m.is_recalled ? <span className="italic opacity-60">Meldingen ble trukket tilbake</span> : m.body}
                  <div className="text-[9px] opacity-50 mt-1">
                    {new Date(m.created_at).toLocaleString("nb-NO")}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex gap-2 p-3 border-t border-white/5">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          className="flex-1 bg-[var(--noir)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] focus:border-[var(--magenta)] focus:outline-none resize-none"
          placeholder={`Svar som @${mock.username}…`}
          data-testid="promo-mock-reply-input"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          className="px-4 bg-[var(--magenta)] text-white hover:opacity-90 disabled:opacity-40"
          data-testid="promo-mock-reply-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function AdminPromoInboks() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true });
  const [openConv, setOpenConv] = useState(null);
  const [tab, setTab] = useState("inbox"); // inbox | profile

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/promo-mock");
      setState({ loading: false, ...r.data });
    } catch (e) {
      setState({ loading: false, error: e.response?.data?.detail || "Feil ved lasting" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "owner") {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-8 text-[var(--cream-soft)]">
          Kun eier har tilgang.
        </div>
      </Layout>
    );
  }

  if (state.loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-8 text-[var(--cream-soft)]">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Laster…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 md:p-8">
        <div className="mb-6">
          <div className="font-meta text-[10px] tracking-[0.3em] text-[var(--magenta)] mb-2">PROMO-INNBOKS</div>
          <h1 className="font-fraunces italic text-3xl md:text-4xl text-[var(--cream)]">
            Styr promo-mock
          </h1>
        </div>

        {!state.mock ? (
          <SetupPanel onCreated={load} />
        ) : (
          <>
            <div className="flex gap-2 mb-6 border-b border-white/10">
              <button
                onClick={() => { setTab("inbox"); setOpenConv(null); }}
                className={`px-4 py-2 text-xs font-meta tracking-[0.15em] border-b-2 ${
                  tab === "inbox" ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-transparent text-[var(--cream-soft)]"
                }`}
                data-testid="promo-mock-tab-inbox"
              >
                INNBOKS {state.unread_total > 0 && (
                  <span className="ml-2 bg-[var(--magenta)] text-white px-1.5 py-0.5 text-[9px]">{state.unread_total}</span>
                )}
              </button>
              <button
                onClick={() => { setTab("profile"); setOpenConv(null); }}
                className={`px-4 py-2 text-xs font-meta tracking-[0.15em] border-b-2 ${
                  tab === "profile" ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-transparent text-[var(--cream-soft)]"
                }`}
                data-testid="promo-mock-tab-profile"
              >
                PROFIL & ALBUM
              </button>
            </div>

            {tab === "inbox" && (
              openConv ? (
                <ChatView
                  conv={openConv}
                  mock={state.mock}
                  onBack={() => { setOpenConv(null); load(); }}
                  onSent={load}
                />
              ) : (
                <InboxPanel mock={state.mock} onOpenConv={setOpenConv} />
              )
            )}

            {tab === "profile" && (
              <ProfileEditor
                mock={state.mock}
                album={state.album}
                images={state.album_images || []}
                onReload={load}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
